import type { ColumnDef } from '@tanstack/react-table';
import UploadStatusBadge from '@/components/UploadStatusBadge';
import DeleteUploadButton from './DeleteUploadButton';
import type { Database } from '@/types/database.types';

export type Upload = Database['public']['Tables']['uploads']['Row'];

export const getColumns = (
  onDelete: (id: string) => void
): ColumnDef<Upload>[] => [
  {
    accessorKey: 'status',
    header: 'Status',
    meta: { className: 'w-40' },
    cell: ({ row }) => {
      return <UploadStatusBadge status={row.getValue('status')} />;
    },
  },
  {
    accessorKey: 'file_name',
    header: 'Name',
    meta: { className: 'w-full' },
    cell: ({ row }) => {
      const name = row.getValue('file_name') as string;
      return (
        <div className="truncate" title={name}>
          {name}
        </div>
      );
    },
  },
  {
    id: 'category',
    accessorFn: (row) => row.content_type.split('/')[0],
    header: 'Type',
    meta: { className: 'w-35' },
    cell: ({ row }) => {
      const category = row.getValue('category') as string;
      const categoryLabels: Record<string, string> = {
        video: 'Video',
        image: 'Image',
        audio: 'Audio',
      };
      return (
        categoryLabels[category] ||
        row.original.content_type.split('/')[1]?.toUpperCase() ||
        row.original.content_type
      );
    },
    enableGrouping: true,
  },
  {
    accessorKey: 'file_size',
    header: 'Size',
    meta: { className: 'w-40' },
    cell: ({ row }) => {
      const bytes = row.getValue('file_size') as number;
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Created at',
    meta: { className: 'w-40' },
    cell: ({ row }) => {
      const date = new Date(row.getValue('created_at') as string);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    },
  },
  {
    id: 'actions',
    meta: { className: 'w-20' },
    cell: ({ row }) => {
      return (
        <DeleteUploadButton
          uploadId={row.original.id}
          fileName={row.original.file_name}
          onDelete={onDelete}
        />
      );
    },
  },
];
